import {
  booleanAttribute,
  Component,
  computed,
  contentChild,
  ElementRef,
  input,
  numberAttribute,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { isFileTypeValid } from '../../utils/file.utils';
import { MibFileSizePipe } from '../../pipes';
import { MibButtonDirective } from '../button';
import { MibCardDirective } from '../card';
import { MibIconComponent, MibIconName } from '../icons';
import { LoaderColors, MibProgressBarComponent } from '../loaders';
import { MibUploadTriggerDirective } from './upload-trigger.directive';
import { MibUploadFile, MibUploadPicked, MibUploadRemoval } from './upload.models';

/** 2 GB. A case video is large, and a limit nobody set should not be the one that stops it. */
const DEFAULT_MAX_SIZE = 2 * 1024 * 1024 * 1024;

/**
 * Hands a file over to the page: an area to drop one into or a control to pick one with, then one
 * row per file with how far along it is.
 *
 * **The component holds nothing.** It says which files the reader chose, the page applies that and
 * hands the list back through {@link files}. That is the rule every controlled component of this
 * library follows, and here it buys something concrete: two uploads on one screen have nothing in
 * common, where a shared store would have made them tread on each other.
 *
 * It does not transfer anything either. Sending bytes, and knowing how many have gone, belongs to
 * the page — the component draws what it is told.
 *
 * ```html
 * <mib-upload accept="video/mp4,video/quicktime" [files]="files()" (picked)="add($event)">
 *   <button mibUploadTrigger mibButton theme="secondary" size="sm">Browse files</button>
 * </mib-upload>
 * ```
 *
 * The chrome around it is not its business: a title, a close cross and a footer of buttons are what
 * `<mib-modal>` is for, and the page assembles the two.
 */
@Component({
  selector: 'mib-upload',
  imports: [MibButtonDirective, MibCardDirective, MibIconComponent, MibProgressBarComponent],
  templateUrl: './upload.component.html',
  /*
   A flex column and not a block: the root below fills it through flex rather than a percentage,
   which never resolved across the chain a modal makes — the same trap the table fell into.
  */
  host: { class: 'flex flex-col' },
})
export class MibUploadComponent {
  /** The files to draw, as the page holds them. The component never changes this list. */
  readonly files = input<readonly MibUploadFile[]>([]);
  /** What the file picker accepts, written as an `<input type="file">` takes it. */
  readonly accept = input('');
  /**
   * The largest file the page will take, in bytes. A bigger one comes back `rejected`.
   *
   * **`Infinity` lifts the ceiling**, and it is the only value that does. `0` is a ceiling of zero
   * bytes and refuses everything; `null`, `undefined` and `''` are not numbers, so `numberAttribute`
   * falls back to the 2 GB default and leaves the limit in place without a word.
   */
  readonly maxSize = input(DEFAULT_MAX_SIZE, { transform: numberAttribute });
  /** Lets the reader pick several at once. */
  readonly multiple = input(false, { transform: booleanAttribute });
  /** Draws the area to drop a file into. Without it, only the trigger is drawn. */
  readonly dropzone = input(true, { transform: booleanAttribute });
  /** What the default trigger says. Ignored when the page brings its own. */
  readonly browseLabel = input('Browse files');
  /**
   * The formats, written for the reader — `'MP4, MOV, MKV'`.
   *
   * It is a sentence and not the `accept` string because the two say different things to different
   * readers: one is for the file picker, the other is what someone has to read before choosing.
   */
  readonly formats = input('');
  /** Takes nothing at all: no pick, no drop, and the trigger says so. */
  readonly disabled = input(false, { transform: booleanAttribute });

  /** The reader picked or dropped files: those the page may take, and those refused with a reason. */
  readonly picked = output<MibUploadPicked>();
  /**
   * The reader wants a row gone — a transfer to stop, or a row they are done with.
   *
   * The component takes nothing out of {@link files} itself, so the page stops the transfer if one
   * is running and drops the row. Without a page listening, the control does nothing.
   */
  readonly removed = output<MibUploadRemoval>();

  private readonly input = viewChild.required<ElementRef<HTMLInputElement>>('picker');
  /** Whether the page brought a trigger: a projection slot cannot be asked whether it was filled. */
  protected readonly trigger = contentChild(MibUploadTriggerDirective);

  protected readonly dragging = signal(false);

  /*
   One file at a time means the choice is made: the area goes once a row is on the list, rather than
   offering a second pick that would silently replace the first. Dropping that row brings it back.
   Several at a time, it stays — another file can join the list whenever the reader wants.
  */
  protected readonly picking = computed(() => this.multiple() || this.files().length === 0);

  /*
   `w-fit` without the drop area, so a lone trigger is as wide as itself rather than stretching
   across its parent. And no container query anywhere: the old root declared one, which made its
   width stop coming from its content — in a flex row with no floor it collapsed to nothing and
   spilled under its neighbour.
  */
  protected readonly rootClasses = computed(
    () => 'flex min-h-0 flex-1 flex-col gap-2' + (this.dropzone() ? '' : ' w-fit'),
  );

  /*
   Without the drop area the same box holds only the trigger, so it takes no skin of its own and
   just sits around it — the click still lands on it, which is what opens the picker.
  */
  protected readonly zoneClasses = computed(() => {
    if (!this.dropzone()) {
      return 'flex w-fit shrink-0 items-center gap-2';
    }
    return [
      'flex shrink-0 flex-col items-center justify-center gap-2 rounded-mib px-5 py-5 text-center',
      'outline-1 -outline-offset-1 outline-dashed',
      this.disabled() ? 'cursor-not-allowed opacity-50 outline-mib-divider' : 'cursor-pointer',
      this.dragging()
        ? 'outline-mib-primary bg-mib-primary/5'
        : 'outline-mib-hairline bg-white' + (this.disabled() ? '' : ' hover:bg-gray-50'),
      'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-solid has-[:focus-visible]:outline-mib-primary',
    ].join(' ');
  });

  protected readonly cardClasses = 'flex flex-col gap-2 [&_[mibButton]]:shrink-0';

  /* One control, two meanings: a transfer still running is stopped, anything else is just dropped. */
  protected removeLabel(item: MibUploadFile): string {
    return (item.state === 'uploading' ? 'Stop importing ' : 'Remove ') + item.name;
  }

  /** The glyph of the row: climbing, finished, or refused — never the tick on all three. */
  protected mark(item: MibUploadFile): MibIconName {
    switch (item.state) {
      case 'done':
        return 'file-check';
      case 'uploading':
        return 'file-up';
      default:
        return 'file-x';
    }
  }

  protected markClasses(item: MibUploadFile): string {
    const tint =
      item.state === 'done' ? 'text-mib-success' : item.state === 'uploading' ? 'text-gray-700' : 'text-mib-danger';
    return `size-5 shrink-0 ${tint}`;
  }

  protected statusClasses(item: MibUploadFile): string {
    const tint = this.failed(item) ? 'text-mib-danger' : item.state === 'done' ? 'text-mib-success' : 'text-gray-500';
    return `shrink-0 text-sm/5 tabular-nums ${tint}`;
  }

  protected tint(item: MibUploadFile): LoaderColors {
    return this.failed(item) ? 'danger' : item.state === 'done' ? 'success' : 'primary';
  }

  /*
   A row that failed draws a full bar rather than the sliding one an unknown progress would give it:
   the transfer stopped, and a bar still running would say the opposite of the word beside it.
  */
  protected progress(item: MibUploadFile): number | null | undefined {
    return this.failed(item) ? (item.progress ?? 100) : item.progress;
  }

  protected failed(item: MibUploadFile): boolean {
    return item.state === 'error' || item.state === 'rejected';
  }

  /** What is written at the end of the row: a figure while it climbs, a word once it has stopped. */
  protected status(item: MibUploadFile): string {
    switch (item.state) {
      case 'done':
        return 'Uploaded';
      case 'error':
        return 'Failed';
      case 'rejected':
        return 'Refused';
      default:
        return item.progress === undefined ? '' : `${Math.round(item.progress)}%`;
    }
  }

  protected browse(): void {
    if (!this.disabled()) {
      this.input().nativeElement.click();
    }
  }

  protected over(event: DragEvent, dragging: boolean): void {
    if (this.disabled() || !this.dropzone()) {
      return;
    }
    /* Both are needed, or the browser opens the file in the tab instead of handing it over. */
    event.preventDefault();
    event.stopPropagation();
    this.dragging.set(dragging);
  }

  protected dropped(event: DragEvent): void {
    this.over(event, false);
    if (!this.disabled() && this.dropzone()) {
      this.take(event.dataTransfer?.files ?? null);
    }
  }

  protected onPick(event: Event): void {
    const element = event.target as HTMLInputElement;
    this.take(element.files);
    /* Cleared, or picking the same file twice in a row fires no change the second time. */
    element.value = '';
  }

  /*
   Sorting happens here and not in the page because the two rules are the component's own inputs:
   what the picker accepts and the largest size it was given. The page is handed both halves rather
   than a filtered list, so a refused file can be shown with its reason instead of vanishing.
  */
  private take(list: FileList | null): void {
    const files = Array.from(list ?? []);
    if (files.length === 0) {
      return;
    }

    const accepted: File[] = [];
    const rejected: MibUploadFile[] = [];

    /*
     Duplicates are refused before the one-file cut below, or a second pick of a file already on the
     list would be dropped by that cut and the page would never hear it was chosen at all.
    */
    const fresh: File[] = [];
    for (const file of files) {
      if (this.alreadyHere(file)) {
        rejected.push(this.refuse(file, this.duplicateError()));
      } else {
        fresh.push(file);
      }
    }

    for (const file of this.multiple() ? fresh : fresh.slice(0, 1)) {
      const wrongType = this.accept() && !isFileTypeValid(file, this.accept());
      const tooBig = file.size > this.maxSize();
      if (wrongType || tooBig) {
        rejected.push(this.refuse(file, wrongType ? this.typeError() : this.sizeError()));
      } else {
        accepted.push(file);
      }
    }

    this.picked.emit({ accepted, rejected });
  }

  /*
   Against the list the page holds right now, and nothing more — there is no memory of what was
   picked earlier. A row taken off the list can therefore be chosen again straight away, which is
   what makes the cross a way to change one's mind rather than a dead end.

   `lastModified` is only compared when the page kept the `File`: without it, a name and a size are
   as close to an identity as a browser gives us.
  */
  private alreadyHere(file: File): boolean {
    return this.files().some((item) => {
      if (item.name !== file.name || item.size !== file.size) {
        return false;
      }
      return item.file ? item.file.lastModified === file.lastModified : true;
    });
  }

  private refuse(file: File, error: string): MibUploadFile {
    return { name: file.name, size: file.size, state: 'rejected', error, file };
  }

  private duplicateError(): string {
    return 'This file is already in the list.';
  }

  private typeError(): string {
    return this.formats() ? `This file is not one of ${this.formats()}.` : 'This kind of file is not accepted.';
  }

  private sizeError(): string {
    return `This file is larger than ${new MibFileSizePipe().transform(this.maxSize())}.`;
  }
}
